import React, { useEffect, useState } from "react";
import {
  Chat,
  Channel,
  MessageList,
  MessageInput,
  Window,
} from "stream-chat-react";
import { useStreamChatClient } from "../context/StreamChatProvider";
import { getFirestore, doc, setDoc, getDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import "stream-chat-react/dist/css/index.css";
import { TypingIndicator } from "./TypingIndicator";
import CustomMessageInput from "./CustomMessageInput";
import { Button, Box, Typography, IconButton, Tooltip } from "@mui/material";
import { logChatPromiseExecution } from "stream-chat";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"; // ← MUIのアイコン読み込み
import Badge from "@mui/material/Badge"; // ← 追加！

import ChannelMembersDrawer from "./ChannelMembersDrawer";
import { onAuthStateChanged } from "firebase/auth";

const PrefectureChat = ({ groupId, region }) => {
  const chatClient = useStreamChatClient();
  const [channel, setChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [key, setKey] = useState(0);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [userInfo, setUserInfo] = useState("");

  const logErrorToFirestore = async (error, additionalInfo = {}) => {
    const auth = getAuth();
    const user = auth.currentUser;

    if (user) {
      try {
        const db = getFirestore();
        const userRef = doc(db, "users", user.uid);
        const userDoc = await getDoc(userRef);

        const errorDetails = {
          errorMessage: error.message,
          errorStack: error.stack,
          ...additionalInfo,
          userId: user.uid,
          userInfo: userDoc.exists() ? userDoc.data() : {},
          userAgent: navigator.userAgent,
          url: window.location.href,
          date: new Date().toISOString(),
        };

        await setDoc(
          doc(db, "errors", `${user.uid}_${Date.now()}`),
          errorDetails
        );
      } catch (firestoreError) {
        console.error("Error logging to Firestore:", firestoreError);
      }
    } else {
      console.error(
        "No authenticated user found. Error not logged to Firestore."
      );
    }
  };

  useEffect(() => {
    const initChat = async () => {
      try {
        if (chatClient) {
          const auth = getAuth();
          const user = auth.currentUser;
          const userId = user ? user.uid : null;

          if (!userId) {
            console.error("User not authenticated");
            return;
          }

          const newChannel = chatClient.channel("messaging", groupId, {
            name: `Group Chat ${groupId}`,
            invite_only: false,
            history: false,
          });

          try {
            await newChannel.query();
          } catch (e) {
            if (e.response && e.response.status === 404) {
              console.log("Channel does not exist, creating new channel.");
              await newChannel.create();
            } else {
              throw e;
            }
          }

          await newChannel.addMembers([userId]);
          await newChannel.watch();
          setChannel(newChannel);

          const response = await newChannel.query({
            messages: { limit: 20, sort: [{ created_at: -1 }] },
          });

          setMessages(response.messages.reverse());
        }
      } catch (error) {
        await logErrorToFirestore(error, { functionName: "initChat" });
      }
    };

    initChat();
  }, [chatClient, groupId]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(getAuth(), async (user) => {
      if (user) {
        const db = getFirestore();
        const userRef = doc(db, "users", user.uid);
        const userDoc = await getDoc(userRef);

        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserInfo(data);
        }
      } else {
        console.warn("⚠️ ログインユーザーがいません");
      }
    });

    return () => unsubscribe(); // クリーンアップ
  }, []);

  const overrideSubmitHandler = async (message) => {
    if (
      !message.text.trim() &&
      (!message.attachments || message.attachments.length === 0)
    ) {
      console.error("空のメッセージや添付ファイルのみは送信できません");
      return;
    }

    try {
      if (channel) {
        const sendMessagePromise = channel.sendMessage(message);
        logChatPromiseExecution(sendMessagePromise, "メッセージ送信");
        setKey((prevKey) => prevKey + 1);
      }
    } catch (error) {
      console.error("メッセージ送信失敗:", error);
    }
  };

  const actions = ["delete", "edit", "react"];

  

  if (!chatClient || !channel) return <div>Loading chat...</div>;

  return (
    <Box
      sx={{
        borderRadius: 2,
        boxShadow: 3,
        backgroundColor: "#fff",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        flex: 1, // ★ 親の高さに伸びる
        minHeight: 0, // ★ MessageList のスクロールを崩さない用
      }}
    >
      {/* ヘッダー */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          py: 1,
          backgroundColor: "#fafafa",
          borderBottom: "5px solid #ddd",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          {region}チャット
        </Typography>

        {userInfo?.isAdmin && (
          <>
            <Tooltip title="メンバーを見る">
              <Badge
                badgeContent={Object.keys(channel.state.members).length}
                color="secondary"
              >
                <IconButton
                  onClick={() => setIsDialogOpen(true)}
                  sx={{
                    ml: 2,
                    transition: "transform 0.3s ease",
                    "&:hover": {
                      transform: "rotate(15deg)",
                    },
                  }}
                  aria-label="メンバーを見る"
                >
                  <InfoOutlinedIcon color="primary" />
                </IconButton>
              </Badge>
            </Tooltip>
          </>
        )}
      </Box>

      {/* チャット本体 */}
      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        <Chat client={chatClient}>
          <Channel channel={channel}>
            <Window>
              <MessageList
                messageActions={actions}
                hideNewMessageSeparator
                showUnreadNotificationAlways
              />

              <Box className="pl-5 py-2 bg-white">
                <TypingIndicator />
              </Box>

              {/* ★ ここを sticky でナビの上に固定する */}
              <Box
                sx={{
                  position: { xs: "sticky", md: "static" }, // モバイルだけ sticky
                  bottom: { xs: 64, md: 0 }, // BottomNav の高さぶん上に
                  zIndex: 5,
                  bgcolor: "#fff",
                }}
              >
                <CustomMessageInput
                  overrideSubmitHandler={overrideSubmitHandler}
                />
              </Box>
            </Window>
          </Channel>
        </Chat>
      </Box>

      {/* メンバーダイアログ */}
      <ChannelMembersDrawer
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        channel={channel}
        currentUser={userInfo}
      />
    </Box>
  );
};

export default PrefectureChat;
